import { useEffect, useState } from "react";
import { MessageCircle, Plus, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const BLANK = { name: "", price: "", messages: "", validity_days: 30, description: "", active: true };

export function AdminWhatsAppPlans() {
  const [plans, setPlans] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [act, setAct] = useState({ seller_id: "", plan_id: "" });

  const load = () => {
    api.get("/admin/whatsapp/plans").then((r) => setPlans(r.data.plans || [])).catch(() => {});
    api.get("/admin/whatsapp/purchases").then((r) => setPurchases(r.data.purchases || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/admin/whatsapp/plans", { ...form, price: Number(form.price), messages: Number(form.messages), validity_days: Number(form.validity_days) });
      toast.success("Plan created"); setForm(BLANK); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const archive = async (p) => { try { await api.delete(`/admin/whatsapp/plans/${p.plan_id}`); toast.success("Plan archived"); load(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } };
  const toggle = async (p) => {
    try { await api.patch(`/admin/whatsapp/plans/${p.plan_id}/toggle`, { active: !p.active }); toast.success(p.active ? "Plan deactivated — hidden from sellers" : "Plan activated — now visible to sellers"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const activate = async (seller_id, plan_id, purchase_id) => {
    if (!seller_id || !plan_id) { toast.error("Seller ID and plan required"); return; }
    try { await api.post("/admin/whatsapp/activate", { seller_id, plan_id, purchase_id }); toast.success("Plan activated for seller"); setAct({ seller_id: "", plan_id: "" }); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-whatsapp-plans">
      <PageHeader title="Bulk WhatsApp Plans" icon={MessageCircle} subtitle="Manage the bulk WhatsApp plans sellers can buy, and manually activate a plan for any seller." />

      {/* Plans */}
      <div className="mb-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="wa-plans-table">
          <thead className="border-b bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="p-3">Plan</th><th className="p-3">Price</th><th className="p-3">Messages</th><th className="p-3">Validity</th><th className="p-3">Status</th><th className="p-3 text-right">On / Off</th></tr></thead>
          <tbody>
            {plans.map((p) => (
              <tr key={p.plan_id} className="border-b border-slate-50" data-testid={`admin-plan-${p.plan_id}`}>
                <td className="p-3 font-semibold">{p.name}</td><td className="p-3">₹{p.price}</td><td className="p-3">{p.messages?.toLocaleString()}</td><td className="p-3">{p.validity_days}d</td>
                <td className="p-3">{p.active ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">Active</span> : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">Inactive</span>}</td>
                <td className="p-3 text-right">
                  <button
                    onClick={() => toggle(p)}
                    data-testid={`toggle-${p.plan_id}`}
                    role="switch"
                    aria-checked={p.active}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${p.active ? "bg-emerald-500" : "bg-slate-300"}`}
                    title={p.active ? "Deactivate (hide from sellers)" : "Activate (show to sellers)"}
                  >
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${p.active ? "translate-x-5" : "translate-x-0.5"}`} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create plan */}
      <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-4" data-testid="create-plan">
        <h3 className="mb-3 flex items-center gap-1.5 text-sm font-bold text-slate-700"><Plus className="h-4 w-4" /> Create a plan</h3>
        <div className="grid gap-2 sm:grid-cols-5">
          <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="plan-name" />
          <Input type="number" placeholder="Price ₹" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="plan-price" />
          <Input type="number" placeholder="Messages" value={form.messages} onChange={(e) => setForm({ ...form, messages: e.target.value })} data-testid="plan-messages" />
          <Input type="number" placeholder="Validity days" value={form.validity_days} onChange={(e) => setForm({ ...form, validity_days: e.target.value })} data-testid="plan-validity" />
          <button onClick={create} data-testid="create-plan-btn" className="rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white">Create</button>
        </div>
        <Input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2" data-testid="plan-desc" />
      </div>

      {/* Manual activation */}
      <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-4" data-testid="manual-activate">
        <h3 className="mb-3 flex items-center gap-1.5 text-sm font-bold text-slate-700"><Check className="h-4 w-4" /> Manually activate for a seller</h3>
        <div className="grid gap-2 sm:grid-cols-3">
          <Input placeholder="Seller user ID" value={act.seller_id} onChange={(e) => setAct({ ...act, seller_id: e.target.value })} data-testid="act-seller" />
          <select value={act.plan_id} onChange={(e) => setAct({ ...act, plan_id: e.target.value })} data-testid="act-plan" className="rounded-lg border border-slate-200 px-3 text-sm">
            <option value="">Select plan…</option>
            {plans.filter((p) => p.active).map((p) => <option key={p.plan_id} value={p.plan_id}>{p.name} · ₹{p.price}</option>)}
          </select>
          <button onClick={() => activate(act.seller_id, act.plan_id)} data-testid="activate-btn" className="rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white">Activate</button>
        </div>
      </div>

      {/* Pending purchase requests */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="pending-purchases">
        <h3 className="mb-3 text-sm font-bold text-slate-700">Pending purchase requests</h3>
        {purchases.length === 0 ? <p className="text-sm text-slate-400">No pending requests.</p> : (
          <div className="space-y-2">
            {purchases.map((p) => (
              <div key={p.purchase_id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-100 px-4 py-2 text-sm" data-testid={`req-${p.purchase_id}`}>
                <span><b>{p.seller_email}</b> · {p.plan_name} · ₹{p.price}</span>
                <button onClick={() => activate(p.seller_id, p.plan_id, p.purchase_id)} data-testid={`approve-${p.purchase_id}`} className="rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white">Confirm & activate</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
